import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "@/lib/theme";
import { ChevronDown, Gift, Image, Share2, Palette, Calendar } from "lucide-react";

export function Unwrap() {
  const navigate = useNavigate();
  const [isUnwrapped, setIsUnwrapped] = useState(false);

  const handleUnwrap = () => {
    setIsUnwrapped(true);
  };

  const handleEnterGallery = () => {
    navigate("/");
  };

  return (
    <div
      style={{
        backgroundColor: "#F5F3F0",
        minHeight: "100vh",
        fontFamily: fonts.serif,
      }}
    >
      <AnimatePresence mode="wait">
        {!isUnwrapped ? (
          /* ========== UNWRAP SCREEN ========== */
          <motion.div
            key="wrapped"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center px-6"
          >
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              style={{
                fontFamily: "'Caveat', cursive",
                fontSize: "1.75rem",
                color: "#A65D57",
                marginBottom: "2rem",
              }}
            >
              Merry Christmas, Amy!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              onClick={handleUnwrap}
              className="cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div
                style={{
                  width: "160px",
                  height: "160px",
                  position: "relative",
                }}
              >
                <Gift
                  size={160}
                  strokeWidth={1}
                  style={{ color: colors.primary }}
                />
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  style={{
                    position: "absolute",
                    top: "-10px",
                    right: "-10px",
                    width: "30px",
                    height: "30px",
                    backgroundColor: "#A65D57",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "1.25rem",
                  }}
                >
                  ✦
                </motion.div>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              style={{
                fontFamily: fonts.sans,
                fontSize: "1.1rem",
                color: colors.textMuted,
                marginTop: "2rem",
              }}
            >
              Tap to unwrap
            </motion.p>
          </motion.div>
        ) : (
          /* ========== REVEALED CONTENT ========== */
          <motion.div
            key="unwrapped"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {/* Section 1: The Reveal */}
            <section className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                <p
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: "1.5rem",
                    color: "#A65D57",
                    marginBottom: "1rem",
                  }}
                >
                  We made you something special...
                </p>

                <h1
                  style={{
                    fontSize: "clamp(2rem, 8vw, 3.5rem)",
                    color: colors.text,
                    letterSpacing: "-0.02em",
                    marginBottom: "0.5rem",
                    lineHeight: 1.1,
                  }}
                >
                  Amy Philp
                  <br />
                  <span style={{ color: colors.primary }}>Sculpture</span>
                </h1>

                <p
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: "1.1rem",
                    color: colors.textMuted,
                    marginTop: "1.5rem",
                  }}
                >
                  Your very own online gallery
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
                className="absolute bottom-12"
              >
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ChevronDown size={32} style={{ color: colors.primaryMuted }} />
                </motion.div>
              </motion.div>
            </section>

            {/* Section 2: What It Does */}
            <section
              className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
              style={{
                background: `radial-gradient(ellipse 80% 60% at 50% 50%, rgba(91, 138, 143, 0.06) 0%, #F5F3F0 100%)`,
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
                className="max-w-md text-center"
              >
                <h2
                  style={{
                    fontSize: "1.75rem",
                    color: colors.text,
                    marginBottom: "2.5rem",
                  }}
                >
                  A home for your art
                </h2>

                <div className="space-y-8">
                  <Feature
                    icon={<Image size={28} />}
                    title="Showcase your work"
                    description="Beautiful displays for your ceramic and porcelain pieces"
                  />
                  <Feature
                    icon={<Share2 size={28} />}
                    title="Easy sharing"
                    description="One link to share with anyone—no Instagram required"
                  />
                  <Feature
                    icon={<Palette size={28} />}
                    title="Totally yours"
                    description="We'll customize everything to match your style"
                  />
                </div>
              </motion.div>
            </section>

            {/* Section 3: What's Next */}
            <section className="min-h-screen flex flex-col items-center justify-center px-6 py-16">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
                className="max-w-md text-center"
              >
                <h2
                  style={{
                    fontSize: "1.75rem",
                    color: colors.text,
                    marginBottom: "1rem",
                  }}
                >
                  What's next?
                </h2>

                <p
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: "1rem",
                    color: colors.textMuted,
                    marginBottom: "2.5rem",
                    lineHeight: 1.7,
                  }}
                >
                  This is just the beginning! We have a few things
                  <br />
                  to do together over the coming months:
                </p>

                <div className="space-y-6 text-left">
                  <NextStep
                    number="1"
                    title="Pick your domain"
                    description="Choose the perfect web address for your gallery (like amyphilp.com)"
                  />
                  <NextStep
                    number="2"
                    title="Design sessions"
                    description="We'll meet a few times to customize colors, layout, and style—whatever feels right to you"
                  />
                  <NextStep
                    number="3"
                    title="Add more work"
                    description="Upload new pieces anytime through your private admin panel"
                  />
                </div>

                <p
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: "1.25rem",
                    color: "#4A6D5C",
                    marginTop: "2.5rem",
                  }}
                >
                  No rush—we'll figure it out together!
                </p>
              </motion.div>
            </section>

            {/* Section 4: Enter Gallery */}
            <section className="min-h-screen flex flex-col items-center justify-center px-6">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
                className="text-center"
              >
                <p
                  style={{
                    fontFamily: "'Caveat', cursive",
                    fontSize: "1.5rem",
                    color: "#A65D57",
                    marginBottom: "1rem",
                  }}
                >
                  Ready to see it?
                </p>

                <h2
                  style={{
                    fontSize: "2rem",
                    color: colors.text,
                    marginBottom: "2rem",
                  }}
                >
                  Enter your gallery
                </h2>

                <motion.button
                  onClick={handleEnterGallery}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    background: colors.primary,
                    color: "white",
                    border: "none",
                    padding: "1rem 2.5rem",
                    fontSize: "1.1rem",
                    fontFamily: fonts.sans,
                    borderRadius: "4px",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(91, 138, 143, 0.3)",
                  }}
                >
                  View Gallery
                </motion.button>

                <p
                  style={{
                    fontFamily: fonts.sans,
                    fontSize: "0.85rem",
                    color: colors.textMuted,
                    marginTop: "3rem",
                    lineHeight: 1.6,
                  }}
                >
                  With love from Andy & Sara,
                  <br />
                  the Bailey Bunch & Tiny Tots
                </p>
              </motion.div>
            </section>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Load Caveat font */}
      <link
        href="https://fonts.googleapis.com/css2?family=Caveat:wght@500&display=swap"
        rel="stylesheet"
      />
    </div>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="flex items-start gap-4 text-left"
    >
      <div style={{ color: colors.primary, flexShrink: 0 }}>{icon}</div>
      <div>
        <h3
          style={{
            fontSize: "1.1rem",
            color: colors.text,
            marginBottom: "0.25rem",
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: "0.9rem",
            color: colors.textMuted,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      </div>
    </motion.div>
  );
}

function NextStep({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="flex items-start gap-4"
    >
      <div
        style={{
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          backgroundColor: colors.primaryLight,
          color: colors.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: fonts.sans,
          fontSize: "0.9rem",
          fontWeight: 500,
          flexShrink: 0,
        }}
      >
        {number}
      </div>
      <div>
        <h3
          style={{
            fontSize: "1.05rem",
            color: colors.text,
            marginBottom: "0.2rem",
          }}
        >
          {title}
        </h3>
        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: "0.85rem",
            color: colors.textMuted,
            lineHeight: 1.5,
          }}
        >
          {description}
        </p>
      </div>
    </motion.div>
  );
}
